import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getTablePreference } from '@repo/util/tablePreferenceStorage';

/** Build a `?a=b&c=d` search string from saved values, skipping empties. */
function buildRestoreSearch(values: Record<string, string>): string | null {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === '' || value == null) continue;
    params.set(key, value);
  }
  const search = params.toString();
  return search ? `?${search}` : null;
}

export type UseFilterRestoreRedirectParams = {
  /** Stable identifier for the table (must match the one used with `useFilterPreference`). */
  tableKey: string;
  /** The current user's id; preferences are stored per user. */
  userId: string;
};

/**
 * Returns a `?…` search string the caller should redirect to (`<Navigate replace>`) when the
 * user lands on a bare table URL and has a saved preference — otherwise `null`.
 *
 * This MUST run in a component that sits ABOVE the table, so the table (and its `Pagination`,
 * which resets `page` whenever the filters change) only mounts once the URL already carries
 * the restored params. Restoring after the table has mounted races that Pagination effect and
 * gets stripped back to defaults.
 */
export function useFilterRestoreRedirect({
  tableKey,
  userId,
}: UseFilterRestoreRedirectParams): string | null {
  const [searchParams] = useSearchParams();
  // Capture the restore target once, at mount.
  const [restoreSearch] = useState(() => {
    const saved = userId ? getTablePreference(userId, tableKey) : null;
    return saved ? buildRestoreSearch(saved) : null;
  });
  const shownRef = useRef(false);

  // Redirect only while the URL is bare (no query string at all) and we haven't yet shown
  // the table. Gating on the URL — not an effect-set flag — guarantees the table never
  // mounts on a bare URL, so nothing can strip the restore.
  const bare = searchParams.toString() === '';
  const redirect = bare && restoreSearch && !shownRef.current ? restoreSearch : null;

  useEffect(() => {
    // Once we render the table (not redirecting), stop auto-restoring so a later Clear
    // (which empties the URL) doesn't bounce back to the saved values.
    if (!redirect) shownRef.current = true;
  });

  return redirect;
}
